import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import OptimusAssistant from "@/pages/OptimusAssistant";
import Dashboard from "@/pages/Dashboard";
import SwipeFile from "@/pages/SwipeFile";
import CampaignBundle from "@/pages/CampaignBundle";
import Recommendations from "@/pages/social/Recommendations";
import MyVideos from "@/pages/social/MyVideos";
import ContentStudio from "@/pages/social/ContentStudio";
import Calendar from "@/pages/Calendar";
import PaidAds from "@/pages/PaidAds";
import EmailMarketing from "@/pages/EmailMarketing";
import SMSMarketing from "@/pages/SMSMarketing";
import WebsiteSEO from "@/pages/WebsiteSEO";
import MediaLibrary from "@/pages/MediaLibrary";
import VFDashboard from "@/pages/vf/Dashboard";
import VFNew from "@/pages/vf/New";
import VFBatch from "@/pages/vf/Batch";
import VFHistory from "@/pages/vf/History";
import Brands from "@/pages/Brands";
import BrandStyle from "@/pages/BrandStyle";
import ApprovalQueue from "@/pages/ApprovalQueue";
import QuickPost from "@/pages/QuickPost";
import BulkPost from "@/pages/BulkPost";
import Team from "@/pages/Team";
import CRM from "@/pages/CRM";
import PublicLead from "@/pages/PublicLead";

// Default landing route per role
const ROLE_HOME = {
  super_admin: "/dashboard",
  brand_admin: "/dashboard",
  marketer: "/dashboard",
  crm_user: "/crm",
  viewer: "/crm",
};

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  // Personal-use app: onboarding is optional, never forced — always go to role home.
  return <Navigate to={ROLE_HOME[user.role] || "/dashboard"} replace />;
};

const OnboardingRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to={ROLE_HOME[user.role] || "/crm"} replace />;
  return <Onboarding />;
};

// Generic role-aware route guard
const RoleGuard = ({ allow, children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!allow.includes(user.role)) return <Navigate to={ROLE_HOME[user.role] || "/crm"} replace />;
  return children;
};

const SUPER = "super_admin";
const BADMIN = "brand_admin";
const MARKETER = "marketer";
const CRMUSER = "crm_user";
const VIEWER = "viewer";
const STAFF = [SUPER, BADMIN, MARKETER];        // content + dashboard access
const CRM_TIERS = [SUPER, BADMIN, CRMUSER, VIEWER];

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/onboarding" element={<OnboardingRoute />} />
          <Route path="/lead/:ownerId/:slug" element={<PublicLead />} />
          <Route path="/" element={<Layout />}>
            <Route index element={<RootRedirect />} />
            <Route path="optimus" element={<RoleGuard allow={[SUPER, BADMIN]}><OptimusAssistant /></RoleGuard>} />
            <Route path="nova" element={<RoleGuard allow={[SUPER, BADMIN]}><OptimusAssistant /></RoleGuard>} />
            <Route path="dashboard" element={<RoleGuard allow={STAFF}><Dashboard /></RoleGuard>} />
            <Route path="swipe-file" element={<RoleGuard allow={STAFF}><SwipeFile /></RoleGuard>} />
            <Route path="campaign-bundle" element={<RoleGuard allow={STAFF}><CampaignBundle /></RoleGuard>} />
            <Route path="social/recommendations" element={<RoleGuard allow={STAFF}><Recommendations /></RoleGuard>} />
            <Route path="social/my-videos" element={<RoleGuard allow={STAFF}><MyVideos /></RoleGuard>} />
            <Route path="social/content-studio" element={<RoleGuard allow={STAFF}><ContentStudio /></RoleGuard>} />
            <Route path="calendar" element={<RoleGuard allow={[SUPER, BADMIN, MARKETER, VIEWER]}><Calendar /></RoleGuard>} />
            <Route path="paid-ads" element={<RoleGuard allow={STAFF}><PaidAds /></RoleGuard>} />
            <Route path="email" element={<RoleGuard allow={STAFF}><EmailMarketing /></RoleGuard>} />
            <Route path="sms" element={<RoleGuard allow={STAFF}><SMSMarketing /></RoleGuard>} />
            <Route path="website/seo" element={<RoleGuard allow={STAFF}><WebsiteSEO /></RoleGuard>} />
            <Route path="media" element={<RoleGuard allow={STAFF}><MediaLibrary /></RoleGuard>} />
            <Route path="video-factory" element={<RoleGuard allow={STAFF}><VFDashboard /></RoleGuard>} />
            <Route path="video-factory/new" element={<RoleGuard allow={STAFF}><VFNew /></RoleGuard>} />
            <Route path="video-factory/new/:videoId" element={<RoleGuard allow={STAFF}><VFNew /></RoleGuard>} />
            <Route path="video-factory/batch" element={<RoleGuard allow={STAFF}><VFBatch /></RoleGuard>} />
            <Route path="video-factory/history" element={<RoleGuard allow={STAFF}><VFHistory /></RoleGuard>} />
            <Route path="brands" element={<RoleGuard allow={[SUPER, BADMIN]}><Brands /></RoleGuard>} />
            <Route path="brands/:brandId/style" element={<RoleGuard allow={[SUPER, BADMIN]}><BrandStyle /></RoleGuard>} />
            <Route path="approval-queue" element={<RoleGuard allow={[SUPER, BADMIN, MARKETER]}><ApprovalQueue /></RoleGuard>} />
            <Route path="quick-post" element={<RoleGuard allow={[SUPER, BADMIN, MARKETER]}><QuickPost /></RoleGuard>} />
            <Route path="bulk-post" element={<RoleGuard allow={[SUPER, BADMIN, MARKETER]}><BulkPost /></RoleGuard>} />
            <Route path="users" element={<RoleGuard allow={[SUPER]}><Team /></RoleGuard>} />
            <Route path="crm" element={<RoleGuard allow={CRM_TIERS}><CRM /></RoleGuard>} />
          </Route>
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
